import React, { useCallback, useEffect, useState } from 'react';
import { tokens } from '@beeline.com/bp-design-system';
import styled, { css } from 'styled-components';

const MIN_WIDTH = 375;
const MAX_WIDTH = 600;

export const ResizeDrawer: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [width, setWidth] = useState(MIN_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Timeout reference for distinguishing clicks
  let clickTimeout: NodeJS.Timeout | null = null;

  const startResizing = useCallback(() => {
    setIsResizing(true);
    setIsDragging(true);
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
    setIsDragging(false);
  }, []);

  const resize = useCallback((mouseMoveEvent: MouseEvent) => {
    const newWidth = Math.min(Math.max(MIN_WIDTH, window.innerWidth - mouseMoveEvent.clientX), MAX_WIDTH);
    setWidth(newWidth);
  }, []);

  const resetToMinWidth = useCallback(() => {
    setWidth(MIN_WIDTH);
  }, []);

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      requestAnimationFrame(() => resize(event));
    };

    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', stopResizing);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (clickTimeout) {
      // If there is already a timeout, this means it's a double click
      clearTimeout(clickTimeout);
      clickTimeout = null;
      // Reset width on double click
      resetToMinWidth();
    } else {
      // Set a timeout to check for single click
      clickTimeout = setTimeout(() => {
        // Start resizing on single click
        startResizing();
        // Clear the timeout
        clickTimeout = null;
      }, 200);
    }
  };

  return (
    <>
      {isResizing && <Overlay />}
      <StyledDocsSidePanel data-test="docs-side-panel" width={width}>
        <Resizer data-test="resizer" isDragging={isDragging} onMouseDown={handleMouseDown} />
        {children}
      </StyledDocsSidePanel>
    </>
  );
};

const StyledDocsSidePanel = styled.div<{ width: number }>`
  position: relative;
  right: 0;
  top: 0;
  display: flex;
  height: 100%;
  width: ${(props) => props.width}px;
  flex-shrink: 0;
  flex-direction: column;
  background-color: ${tokens.colorOnPrimary};
  z-index: 99;
`;

const Resizer = styled.div<{ isDragging: boolean }>`
  width: 10px;
  cursor: ew-resize;
  background-color: transparent;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 100;

  &:hover {
    border-left: 3px solid ${tokens.colorOutline};
  }

  ${(props) =>
    props.isDragging &&
    css`
      border-left: 3px solid ${tokens.colorOutline};
    `}
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: ew-resize;
  z-index: 101;
`;
